import type { CountryCode } from 'libphonenumber-js/max';
import type {
  AssetDto,
  BranchDto,
  BranchDtoBranchStatusEnumKey,
  BranchDtoBranchTypeEnumKey,
  BrokerageFeeOnPolicy,
  BrokerageFirm,
  Customer,
  CustomerDto,
  DerogationStatusEnumKey,
  DerogationWithPolicyAndCustomerEntity,
  ErrorDictionary,
  FeatureFlagControllerAtMeQueryParamsKeyEnumKey,
  ForUserError,
  GetBrokerageFirmOutput,
  GetBrokerageFirmOutputFirmTypeEnumKey,
  I18nResolver,
  ListBrokerageFirmDerogationsInputBody,
  ListBrokerageFirmPoliciesInputBody,
  ListChildBrokerageFirmsOutputData,
  Policy,
  PolicyBranchesForBrokerEntity,
  PolicyDto,
  PolicyDtoStatusEnumKey,
} from '..';
import type {
  AmountDto,
  BreakdownAmounts,
  PaymentMethodDtoWithIsPreferred,
  PolicyDocumentsDto,
  PropertyCondition,
  QuoteComponentDto,
} from '../generated/kubb';
import type { ConditionalVisibility, Field } from './Forms.types';
import type { CustomFunnelConfig, FunnelStepsConfig, UniqAppConfig } from './UniqAppConfig.types';

export type CellType = 'price' | 'percentage' | 'text';

type FilterField =
  | 'businessProvider'
  | 'policyStatus'
  | 'creationDate'
  | 'endDate'
  | 'policyCustomStatus'
  | 'derogationStatus';

export type Filter = {
  field: FilterField;
  value: string | ReadonlyArray<string> | { start?: string; end?: string } | null;
};

export type TeamColumnConfig = {
  name: string;
  label?: string;
  accessor: (team: ListChildBrokerageFirmsOutputData) => string | undefined | null;
};

// MethodPaymentConfig
type MethodPaymentConfig = {
  stripePublishableKey: string;
};

type PolicyDocumentsConfig = {
  policyDocumentsList: {
    label: string;
    category: string;
  }[];
};

type IntermediaryUserManagementConfig = {
  showTeamManagement?: ReadonlyArray<GetBrokerageFirmOutputFirmTypeEnumKey>;
  usersTabTitle?: string;
  teamsTabTitle?: string;
};

type TeamCreationField = Field extends infer F
  ? F extends Field
    ? Omit<F, 'label' | 'placeholder'>
    : never
  : never;

type TeamManagementConfig = {
  teamCreationTeamFields?: Array<TeamCreationField>;
  teamCreationFirstBrokerFields?: Array<TeamCreationField>;
  inviteRole?: {
    WHOLESALE?: string[];
    RETAIL?: string[];
    TEAM?: string[];
  };
  columns?: ReadonlyArray<TeamColumnConfig>;
} & (
  | {
      sendInviteOnCreation: true;
      inviteCustomFieldMapping: {
        email: string;
        firstName: string;
        lastName: string;
      };
    }
  | {
      sendInviteOnCreation?: false | undefined;
      inviteCustomFieldMapping?: never;
    }
);

// OngoingNewBusinessConfig
// Pseudo-status for the status filter: cover continues until the stop runs on its own date.
export const STOP_SCHEDULED = 'STOP_SCHEDULED';

// Pseudo-status for the status filter: a stop whose proof no manager has decided yet.
export const STOP_AWAITING_VALIDATION = 'STOP_AWAITING_VALIDATION';

// Pseudo-status for the status filter (payment-gated product, signed, start date passed, unpaid).
export const AWAITING_FIRST_PAYMENT = 'AWAITING_FIRST_PAYMENT';

export const DEROGATION_APPROVED = 'DEROGATION_APPROVED';

export type SelectFilterConfig = {
  type: 'select';
  displayName?: string;
  inputPlaceholder?: string;
  options: ReadonlyArray<string>;
  onSameBranchWith?: PropertyCondition;
};

export type DateFilterConfig = {
  type: 'date';
  displayName?: string;
};

export type BooleanFilterConfig = {
  type: 'checkbox';
  displayName?: string;
  label?: string;
};

export type TextInputFilterConfig = {
  type: 'textInput';
  displayName?: string;
  inputPlaceholder?: string;
};

export type AssignedBrokerFilterConfig = {
  type: 'assignedBrokerSelect';
  displayName?: string;
  inputPlaceholder?: string;
};

export type FilterConfig =
  | SelectFilterConfig
  | DateFilterConfig
  | BooleanFilterConfig
  | TextInputFilterConfig
  | AssignedBrokerFilterConfig;

export type DateFormat = 'date' | 'datetime';

type BasePolicyColumnConfig = {
  name: string;
  label?: string;
} & (
  | {
      type: 'string';
    }
  | {
      type: 'boolean';
    }
  | {
      type: 'date';
      format?: DateFormat;
    }
  | {
      type: 'price';
    }
  | {
      type: 'status';
      statuses?: Record<string, string>;
    }
  | {
      type: 'product';
    }
  | {
      type: 'tier';
    }
  | {
      type: 'fraudStatus';
    }
);

type PolicyColumnConfigWithPath = BasePolicyColumnConfig & {
  path: string;
};

type PolicyColumnConfigWithCustomField = BasePolicyColumnConfig & {
  customField: 'customers' | 'policy';
};

type PolicyColumnConfigWithAccessor = BasePolicyColumnConfig & {
  accessor: (
    input: PolicyBranchesForBrokerEntity,
  ) => string | number | boolean | Date | undefined | null;
};

export type PolicyColumnConfig =
  | PolicyColumnConfigWithPath
  | PolicyColumnConfigWithCustomField
  | PolicyColumnConfigWithAccessor;

type BaseDerogationColumnConfig = {
  name: string;
} & (
  | {
      type: 'string';
    }
  | {
      type: 'boolean';
    }
  | {
      type: 'date';
      format?: DateFormat;
    }
  | {
      type: 'status';
      statuses?: Record<string, string>;
      translationNamespace?: string;
    }
  | {
      type: 'product';
    }
);

type DerogationColumnConfigWithPath = BaseDerogationColumnConfig & {
  path: string;
};

type DerogationColumnConfigWithAccessor = BaseDerogationColumnConfig & {
  accessor: (
    input: DerogationWithPolicyAndCustomerEntity,
  ) => string | number | boolean | Date | undefined | null;
};

export type DerogationColumnConfig =
  | DerogationColumnConfigWithPath
  | DerogationColumnConfigWithAccessor;

export type OngoingNewBusinessConfig = {
  expandCustomers?: boolean;
  expandAssets?: boolean;
  expandBrokerageFirm?: boolean;
  filtersConfig: Record<string, FilterConfig>;
  baseFilters: ListBrokerageFirmPoliciesInputBody;
  columns: ReadonlyArray<PolicyColumnConfig>;
  initialFilters?: Filter[];
};

// PortfolioConfig
export type PortfolioConfig = {
  expandCustomers?: boolean;
  expandBrokerageFirm?: boolean;
  expandAssets?: boolean;
  filtersConfig: Record<string, FilterConfig>;
  baseFilters: ListBrokerageFirmPoliciesInputBody;
  columns: ReadonlyArray<PolicyColumnConfig>;
};

// AccountingDashboardConfig
export type AccountingDashboardConfig = {
  filtersConfig: {
    policyStatus?: SelectFilterConfig;
    terminationReason?: SelectFilterConfig;
  } & Record<string, FilterConfig>;
};
// DerogationValidationsConfig
export type DerogationValidationsConfig = {
  // Only the event-sourced statuses reach the badge: the column reads branch.derogations[].status.
  derogationStatuses?: Record<DerogationStatusEnumKey, string>;
  filtersConfig: Record<string, FilterConfig>;
  baseFilters: ListBrokerageFirmDerogationsInputBody;
  columns: Array<DerogationColumnConfig>;
  initialFilters?: Filter[];
};

export interface DerogationValidationsModuleProps {
  baseFilters: ListBrokerageFirmDerogationsInputBody;
  columns?: Array<DerogationColumnConfig>;
  filtersConfig?: Record<string, FilterConfig>;
  initialFilters?: Filter[];
}

// PolicyStatusConfig
type PolicyStatusConfig = {
  statuses: Record<string, string>;
};

export type CustomerDisplay = {
  firstName?: string;
  lastName?: string;
  companyName?: string;
};

// DataAccessorsConfig
type DataAccessorsConfig = {
  // biome-ignore lint/suspicious/noExplicitAny: A real `any`, accepts anythings as long as it returns a string or null
  customerDisplayName?: (customer: any) => string | null;
};

type DocumentsConfig = {
  uploadModes: {
    policyholderDocuments: 'category' | 'batch';
    nonDelegatedDocuments: 'category' | 'batch';
    brokerageFirmDocuments: 'category' | 'batch';
  };
};

export type AIClassificationQualityFlag =
  | 'corrupted_document'
  | 'multiple_pages'
  | 'poor_image_quality'
  | 'document_oriented_incorrectly'
  | 'partially_visible'
  | 'language_mismatch'
  | 'handwritten_sections';

export type AIClassificationDisplayConfig = {
  enabled: boolean;
  showQualityFlags: boolean;
  qualityFlagsToShow: AIClassificationQualityFlag[];
  showReclassificationEvents: boolean;
  showJudgeVerdicts: boolean;
  productId?: string;
};

type AIClassificationConfig = {
  interfaces: {
    policyholderDashboard?: AIClassificationDisplayConfig;
    nonDelegatedDocuments?: AIClassificationDisplayConfig;
    brokerageFirmDocuments?: AIClassificationDisplayConfig;
  };
};

type IntermediaryUserCreationConfig = {
  title?: string;
  inviteRole: {
    WHOLESALE?: string[];
    RETAIL?: string[];
    TEAM?: string[];
  };
};

export type ClaimsConfig = {
  claimsCategories: Array<{
    category: string;
    label: string;
    hasResponsibility?: boolean;
  }>;
  fieldsConfig?: Field[];
};

export type InformationFieldParams = {
  key: string;
  type?: 'text' | 'number' | 'boolean' | 'date' | 'email' | 'tel' | 'siret' | 'postcode';
  countries?: CountryCode[];
  formatValue?: (value: string | number | boolean | undefined) => string | undefined;
  hideIfEmpty?: boolean;
  contactKey?: string;
  forceReadonly?: boolean;
};

export type InformationSeparatorParams = {
  type: 'separator';
  id?: string;
  hideIfFieldEmpty?: string;
};

export type InformationCardConfig = {
  title?: string;
  fieldsConfig: Array<string | InformationFieldParams | InformationSeparatorParams>;
};

export type CustomerInformationCardConfig = {
  fieldsConfig: Array<string | InformationFieldParams | InformationSeparatorParams>;
};

export type AssetInformationCardConfig = {
  fieldsConfig: Array<string | InformationFieldParams | InformationSeparatorParams>;
  // Dashboard layout for the asset list. Defaults to 'cards'; 'table' suits fleets.
  display?: 'cards' | 'table';
};

export type SimpleQuote = {
  totalPremium: AmountDto;
  components: QuoteComponentDto[];
};

export type QuoteAccessor = (input: {
  firmType?: GetBrokerageFirmOutputFirmTypeEnumKey;
  retailBrokerageFirmId?: string;
  wholesaleBrokerageFirmId?: string;
  policyQuote?: SimpleQuote;
  assetQuote?: SimpleQuote;
  assetQuotes?: SimpleQuote[];
  brokerageFees?: BrokerageFeeOnPolicy[];
}) => number | undefined;

export type CustomIncludedPerilsProps = {
  tier?: string;
  includedPerils: Array<{ id: string; unavailabilityMessage?: string }>;
  unavailabilityMessage?: string;
};

export type QuoteTierSelectModuleConfig = {
  customIncludedPerils?: (props: CustomIncludedPerilsProps) => React.ReactNode;
  /** Peril IDs to display as optional toggleable options (e.g., "Perte pécuniaire: incluse / non-incluse") */
  optionalPerilIds?: string[];
  /** When defined, renders an excess selector above the tier cards and filters cards by the selected excess */
  excessOptions?: Array<{ value: string; label: string }>;
  /** Folds each card's peril list behind a shared show/hide toggle */
  collapseIncludedPerils?: boolean;
};

type BreakdownSource = 'total' | 'recurring' | 'oneTime';
type GetPriceBreakdownPath = `${BreakdownSource}.${keyof BreakdownAmounts}`;
export type PresetKey = GetPriceBreakdownPath;

export type PriceDisplaySubLine = { type: 'PRESET'; key: PresetKey; variant?: 'bold' | 'dimmed' };

export type PriceDisplayLine =
  | { type?: 'CUSTOM'; key: string; variant?: 'bold' | 'dimmed'; tooltip?: boolean }
  | {
      type: 'PRESET';
      key: PresetKey;
      variant?: 'bold' | 'dimmed';
      tooltip?: boolean;
      subLines?: PriceDisplaySubLine[];
    };

export type PriceDisplayTotal = { type: 'PRESET'; key: PresetKey };

/**
 * Controls how premium pricing is displayed across PremiumModule, PremiumCompareModule,
 * and QuoteRecapModule.
 *
 * When no groups are configured, modules fall back to displaying
 * PRE TAXES, Taxes, and TOTAL.
 */
export type PriceDisplayConfig = {
  /** Static description shown below the card header (used by QuoteRecapModule). */
  description?: string;

  /**
   * Ordered line groups for PremiumModule and PremiumCompareModule.
   * Each group can optionally render as a visual callout block.
   * Lines can mix CUSTOM keys (defined in the backend) and PRESET keys
   * (computed fields like `totalPremiumPreTaxes` from GetPriceOutput).
   */
  groups?: Array<{
    variant?: 'callout';
    lines: PriceDisplayLine[];
  }>;

  /** Simplified groups for the quote recap sidebar. When set, QuoteRecapModule uses these instead of `groups`. */
  quoteRecapOverride?: Array<{
    variant?: 'callout';
    lines: PriceDisplayLine[];
  }>;

  /** Total rows displayed at the bottom of the price table (e.g. total premium, first invoice, renewal). Falls back to the API's `totalLine`, or recurring TTC if no display lines exist. */
  totals?: PriceDisplayTotal[];
};

export type DelegatedQuoteModuleConfig = {
  perilAmounts?: Record<string, number | QuoteAccessor>;
  showPerilOptions?: boolean;
  perilOptionsFilter?: Record<
    string,
    (args: { options: string[]; policy?: Policy; customer?: Customer }) => string[]
  >;
  /** Peril forced off and disabled while the feature flag is on */
  lockedPeril?: { id: string; featureFlag: FeatureFlagControllerAtMeQueryParamsKeyEnumKey };
};

/**
 * Configuration for the QuoteTierModule component that displays selected insurance tier
 * and coverage options for the current vehicle.
 *
 * @example
 * ```typescript
 * quoteTierModuleConfig: {
 *   title: 'Formules et options sélectionnées (pour le véhicule en cours)',
 *   goToTiersOnMta: '/mta/${policy.customer}/${policy.id}/new',
 *   goToTiersOnNewBusiness: '/discovery/${policy.customer}/${policy.id}/new',
 *   fieldsConfig: [
 *     {
 *      label: 'Rabais commercial',
 *       path: 'customFields.commercialRate',
 *       type: 'percentage',
 *     },
 *     {
 *       label: 'Taux de commissions',
 *       path: 'customFields.commissionRate',
 *       type: 'percentage',
 *     },
 *     {
 *       label: 'Frais de dossier TTC',
 *       path: 'customFields.managementFeeTaxesIncluded',
 *       type: 'price',
 *     },
 *   ],
 * },
 * ```
 */
type QuoteTierModuleConfig = {
  /**
   * URL template for navigating to tier selection when working on a branch.
   * Supports template variables: ${policy.customer}, ${policy.id}, ${workingBranch.id}
   */
  goToTiersOnMta: string;
  /**
   * URL template for navigating to tier selection during new business creation.
   * Supports template variables: ${policy.customer}, ${policy.id}
   */
  goToTiersOnNewBusiness: string;

  /**
   * The custom fields to display in the module.
   */
  fieldsConfig: Array<{
    label: string;
    path: string;
    type: CellType;
  }>;
  /** Mapping of perils to their display names */
  perilsMapping: Record<string, string[]>;
  /** Mapping of peril IDs to their policy custom field name, for perils whose option is stored
   *  as a policy custom field rather than on the peril's `option` field. */
  perilCustomFieldsMapping?: Record<string, string>;
  /**
   * Whether to display the external quote module.
   */
  isExternalQuoteEnabled: boolean;

  /**
   * Whether to display the quote tier module.
   */
  isChosenTierDisplayed: boolean;

  isCompareDisabled?: boolean;

  /**
   * Custom component to render the included perils list, replacing the default peril list.
   */
  customIncludedPerils?: (props: CustomIncludedPerilsProps) => React.ReactNode;
};

/**
 * Configuration for the Policy Lifecycle Summary module.
 * This module displays key dates in a policy's lifecycle.
 */
type PolicyLifecycleSummaryModuleConfig = {
  /**
   * The date format for displaying dates, compatible with dayjs.
   * @example 'DD/MM/YYYY HH:mm'
   * @see https://day.js.org/docs/en/display/format
   */
  dateDisplayFormat: string;
};

export type RedirectRules = {
  hasUserProducts?: string;
  hasPendingDistributionRequest?: string;
  noDistributionRequest?: (brokerageFirmId: string) => string;
  default: (policyId?: string) => string;
};

type BusinessRolesConfig = {
  redirectRules: RedirectRules;
};

type ComplianceModuleConfig = {
  enableManualAlert: boolean;
};

type PolicyholderInvitationModuleConfig = {
  relationTypes: string[];
};

type SignatureOptions = {
  isOtpEnabled: boolean;
};

/** The customers actually billed for the policy, each with their own payment methods. */
export type RulePayer = {
  customer: Customer;
  paymentMethods: PaymentMethodDtoWithIsPreferred[];
};

export type ValidationRule = {
  label: string;
  isValid: (input: {
    policy: Policy;
    customer: Customer;
    customers: Customer[];
    payers: RulePayer[];
    documents: PolicyDocumentsDto[];
    brokerageFirm?: BrokerageFirm;
    paymentMethods: PaymentMethodDtoWithIsPreferred[];
    hasUnacknowledgedAlerts: boolean;
    branch?: BranchDto;
    needsDerogation?: boolean;
  }) => boolean;
  message?: string;
};

export type FinalizeNewBusinessModuleRule = Omit<ValidationRule, 'label' | 'message'> & {
  /**
   * Semantic identifier used to derive translation keys
   * `finalizeNewBusiness.rules.<id>.{label,message}`.
   */
  id: string;
  link?: string | ((params: { policyId: string; branchId: string; customerId: string }) => string);
  /** Keeps the rule only while the flag matches `when`; absent means the rule always applies. */
  featureFlag?: { id: FeatureFlagControllerAtMeQueryParamsKeyEnumKey; when: boolean };
};

export type FunnelSignatureTypeConfiguration = {
  policyHolderSignatureType: 'policyholderFunnel';
  customEventType: string;
};

export type ButtonSignatureType = 'direct' | 'policyholderFunnel';
export type ButtonDeliveryMode = 'EMAIL' | 'URL';

export type ButtonVisualConfig = {
  /**
   * @deprecated Omit and let the consumer derive the label from the parent
   * button's `id` via `finalizeNewBusiness.buttons.<id>.label`.
   */
  label?: string;
  info?: string;
  icon: 'mail' | 'pen' | 'x';
  /**
   * @deprecated Omit and let the consumer derive the success message from the
   * parent button's `id` via `finalizeNewBusiness.buttons.<id>.successMessage`.
   */
  successMessage?: string;
};

type BaseOtpConfig = {
  enabled: boolean;
  phoneCustomField?: string;
  redirectUrl?: string;
};

type DirectEmailSignatureConfig = {
  signatureType: 'direct';
  deliveryMode: 'EMAIL';
  otp: BaseOtpConfig;
};

type DirectUrlSignatureConfig = {
  signatureType: 'direct';
  deliveryMode: 'URL';
  otp: BaseOtpConfig;
};

type FunnelSignatureConfig = {
  signatureType: 'policyholderFunnel';
  customEventType: string;
};

export type SignatureConfig =
  | DirectEmailSignatureConfig
  | DirectUrlSignatureConfig
  | FunnelSignatureConfig;

export type ButtonVisibilityCondition = {
  firmTypes?: GetBrokerageFirmOutputFirmTypeEnumKey[];
  policyStatuses?: PolicyDtoStatusEnumKey[];
  branchStatuses?: BranchDtoBranchStatusEnumKey[];
  branchTypes?: BranchDtoBranchTypeEnumKey[];
  derogationStatuses?: DerogationStatusEnumKey[];
  needsDerogation?: boolean;
};

type BaseButtonConfig = {
  id: string;
  visual: ButtonVisualConfig;
  rules?: Array<FinalizeNewBusinessModuleRule>;
  visibilityConditions?: ButtonVisibilityCondition;
};

export type SignatureButtonConfig = BaseButtonConfig & {
  actionType: 'signature';
  signature: SignatureConfig;
};

export type ConfirmButtonConfig = BaseButtonConfig & {
  actionType: 'confirm';
};

export type DeclineDerogationButtonConfig = BaseButtonConfig & {
  actionType: 'decline-derogation';
};
export type SendContractButtonConfig =
  | SignatureButtonConfig
  | ConfirmButtonConfig
  | DeclineDerogationButtonConfig;

export type DirectSignatureTypeConfiguration = {
  policyHolderSignatureType: 'direct';
};

export type FinalizeNewBusinessModuleConfig = {
  rules?: Array<FinalizeNewBusinessModuleRule>;
  /**
   * @deprecated
   */
  buttons?: SendContractButtonConfig[];
};

export type ClaimsReviewSection = {
  /** Discriminant of the section title, translated as `<productId>.claimsReview.sections.<id>` */
  id: string;
  claimsMetadata?: Record<string, string>;
  /** Shown when true on the policy of at least one compared branch */
  showWhen?: Array<ConditionalVisibility & { apiProperty: string; fieldName?: never }>;
};

export type ClaimsReviewConfig =
  | { attachedTo: 'policy'; sections?: ClaimsReviewSection[] }
  | { attachedTo: 'assets' };

export type PolicyDashboardEventsModuleConfig = {
  enableAssetComparison: boolean;
  enablePolicyComparison?: boolean;
  /** Lists the claims, read-only, in the compare dialog; absent = not shown */
  claimsReview?: ClaimsReviewConfig;
  quoteDerogationFunnelModule?: FunnelStepsConfig;
};

export type ProductDisplayConfig = {
  category: string;
  icon: React.ComponentType<{ className?: string }> | ((props: { className?: string }) => unknown);
  badges: string[];
};

export type GeneratedDocumentGroupConfig = {
  id: string;
  categories: readonly string[];
};

export type GeneratedDocumentsDisplayConfig = {
  groups: readonly GeneratedDocumentGroupConfig[];
};

export type KorintModulesProductsConfig = {
  display: ProductDisplayConfig;
  customerDefaultRole?: string;
  productCustomPath: string;
  enableAssetComparison?: boolean;
  isWakamErrorEnabled?: boolean;
  /** Show each guarantee's excess, read from `perils.<id>.excess`, on the guarantees table
   *  and on the quote page. Off by default: a product with no declared excess would
   *  otherwise render its translation keys. */
  isGuaranteeExcessDisplayed?: boolean;
  /** Block progression past the quote step (and show an error) when the computed premium is 0. */
  blockOnZeroPremium?: boolean;
  customerInformations?: CustomerInformationCardConfig;
  assetInformations?: AssetInformationCardConfig;
  policyInformationCard?: InformationCardConfig;
  policyholderPolicyInformationCard?: InformationCardConfig;
  policyholderContactsModuleConfig?: PolicyholderContactsModuleConfig;
  policyholderClaimsDeclarationModuleConfig?: PolicyholderClaimsDeclarationModuleConfig;
  claims?: ClaimsConfig;
  quoteTierModuleConfig?: QuoteTierModuleConfig;
  policyholderInvitationModuleConfig?: PolicyholderInvitationModuleConfig;
  policyholderDiscoveryFunnelSteps?: FunnelStepsConfig;
  policyholderMtaFunnelSteps?: FunnelStepsConfig;
  discoveryFunnelSteps?: FunnelStepsConfig;
  mtaFunnelSteps?: FunnelStepsConfig;
  renewalFunnelSteps?: FunnelStepsConfig;
  policyholderRenewalFunnelSteps?: FunnelStepsConfig;
  quoteTierSelectModuleConfig?: QuoteTierSelectModuleConfig;
  priceBreakdownConfig?: PriceDisplayConfig;
  delegatedQuoteModuleConfig?: DelegatedQuoteModuleConfig;
  stopReasonsMapping?: Record<string, string>;
  signatureOptions?: SignatureOptions;
  finalizeNewBusinessModuleConfig?: FinalizeNewBusinessModuleConfig;
  brokerPolicyDashboardHome?: {
    isFinalizeNewBusinessModuleVisible?: boolean;
    isAuthorCardModuleVisible?: boolean;
    isPolicyholderInvitationModuleVisible?: boolean;
    isQuoteTierModuleVisible?: boolean;
    isPremiumModuleVisible?: boolean;
    isPolicyLifecycleSummaryModuleVisible?: boolean;
    isInvoicesBalanceModuleVisible?: boolean;
    disableDocumentsValidation?: boolean;
    customModules?: Array<() => React.ReactNode>;
    preQuoteTierModules?: Array<() => React.ReactNode>;
  };
  /** Document types listed on the policy documents page on top of the ones the backend config declares. */
  additionalDocumentTypes?: string[];
  generatedDocumentsDisplay?: GeneratedDocumentsDisplayConfig;
  dashboardAssetFieldKey?: string;
  /** Extra policy-dashboard banners, for alerts whose data folio cannot reach. */
  policyWarningBanners?: Array<() => React.ReactNode>;
  policyDashboardEventsModuleConfig?: PolicyDashboardEventsModuleConfig;
  customFunnels?: ReadonlyArray<CustomFunnelConfig>;
  /** Pages served to an anonymous session (same guard as anonymous funnels), e.g. a post-signature view. */
  anonymousPages?: ReadonlyArray<{ path: string; element: React.ReactNode }>;
  redirectDisplayName?: (data: {
    assets: AssetDto[];
    policy: PolicyDto;
    customer: CustomerDto;
  }) => string;
};

type PolicyholderContactsModuleConfig = {
  title?: string;
  description?: string;
  displayBrokerageFirmContact?: boolean;
  contacts?: Array<{
    type: 'email' | 'phone' | 'website' | 'mail' | 'info';
    showIcon: boolean;
    label?: string;
    prefix?: string;
    value: string;
    extraInfo?: string;
  }>;
};

type PolicyholderClaimsDeclarationModuleConfig = {
  title: string;
  /** Shown only while this flag is on for the policy's product. */
  featureFlag?: FeatureFlagControllerAtMeQueryParamsKeyEnumKey;
  description?: string;
  cta?: {
    label: string;
    /** A URL starting with `/` is an in-app route; `:policyId` is the dashboard's policy. */
    url: string;
  };
  contacts?: Array<{
    type: 'email' | 'phone' | 'mail' | 'info';
    showIcon: boolean;
    label?: string;
    prefix?: string;
    value: string;
    extraInfo?: string;
  }>;
};

export type IntermediaryAdditionalInformationField = Field & {
  /**
   * Optional message to display when the field is required but not filled.
   * This message will be shown in tooltip on disabled buttons.
   */
  message?: string;
};

type DistributionRequestListModuleConfig = {
  /**
   * The fields that should be filled by the Wholesale Broker regarding an intermediary
   */
  intermediaryAdditionalInformationFields: IntermediaryAdditionalInformationField[];
};

type AssetClaimsConfig = {
  claimsMetadata?: Record<string, string>;
};

type IntermediaryGeneralModuleConfig = {
  /**
   * Whether to display the inscription link button for intermediaries
   */
  displayInscriptionLink: boolean;
};

export type KorintModulesConfig = {
  methodPayment?: MethodPaymentConfig;
  policyDocuments?: PolicyDocumentsConfig;
  ongoingNewBusiness?: OngoingNewBusinessConfig;
  policyStatus?: PolicyStatusConfig;
  portfolio?: PortfolioConfig;
  accountingDashboard?: AccountingDashboardConfig;
  derogationValidations?: DerogationValidationsConfig;
  documents?: DocumentsConfig;
  aiClassification?: AIClassificationConfig;
  intermediaryUserCreation?: IntermediaryUserCreationConfig;
  intermediaryUsersList?: { title?: string };
  intermediaryUserInvitesList?: { title?: string };
  brokerageFirmInformationCard?: InformationCardConfig;
  policyLifecycleSummaryModuleConfig?: PolicyLifecycleSummaryModuleConfig;
  businessRolesConfig?: Record<string, BusinessRolesConfig>;
  complianceModuleConfig?: ComplianceModuleConfig;
  distributionRequestListModule?: DistributionRequestListModuleConfig;
  intermediaryUserManagement?: IntermediaryUserManagementConfig;
  teamManagement?: TeamManagementConfig;
  policyholderContactsModuleConfig?: PolicyholderContactsModuleConfig;
  policyholderClaimsDeclarationModuleConfig?: PolicyholderClaimsDeclarationModuleConfig;
  assetClaims?: AssetClaimsConfig;
  intermediaryGeneralModule?: IntermediaryGeneralModuleConfig;
  intermediaryDetailsLayout?: {
    buildBrokerTitle?: (data: {
      companyInfo: GetBrokerageFirmOutput['companyInfo'];
      customFields: Record<string, unknown>;
    }) => string | undefined;
  };
};

export type KorintConfig = {
  /**
   * The configuration for the Uniq app.
   */
  uniqAppConfig?: UniqAppConfig;

  /**
   * Renders one "new quote" button per product instead of a single button
   * opening a searchable list. Only applies when the broker has 2 or 3 products:
   * a single product already goes straight into the funnel, and 4 or more hand
   * over to the product selection page. Opt-in per tenant.
   */
  newBusinessButtonPerProduct?: boolean;

  /**
   * The configuration for the products.
   */
  productsConfig?: Record<string, KorintModulesProductsConfig>;

  /**
   * The configuration for modules.
   */
  modulesConfig?: KorintModulesConfig;

  /**
   * Overrides folio's default palette, for a tenant whose statuses or colors differ.
   */
  policyStatuses?: Record<string, string>;

  /**
   * Overrides folio's default signature and signer palette, shared by both.
   */
  signatureStatuses?: Record<string, string>;

  /**
   * Optional function to override policy status based on stop reason.
   * This allows products to customize how stopped policies are displayed.
   *
   * @param status - The current policy status
   * @param stopReason - The stop reason if the policy is stopped
   * @returns The status to display (can be a custom status like POLICY_TERMINATED), or undefined to use the original status
   */
  getPolicyStatusOverride?: (
    status: PolicyDtoStatusEnumKey,
    stopReason?: string,
  ) => string | undefined;

  /**
   * The configuration for data accessors.
   */
  dataAccessors: DataAccessorsConfig;

  /**
   * The configuration for errors.
   */
  errorsConfig: {
    defaultErrorMessage: ForUserError;
    errorDictionary: ErrorDictionary;
    i18n?: I18nResolver;
  };

  /**
   * Business roles allowed to see the support link in the account menu.
   * When omitted, the support link is visible to all users.
   */
  supportLinkRoles?: ReadonlyArray<string>;
};
